/**
 * useUserOrgMemberships — list of every active org the current user
 * is a member of, with the org's display name + slug. Powers the
 * org-switcher header widget.
 *
 * RLS-scoped: user_org_memberships.SELECT allows reads of own rows
 * (per migration 20260514000002), so the user always sees their own
 * memberships even if the active org's get_my_org_id() narrows the
 * other org-tables.
 *
 * The implicit join through organizations is allowed because every
 * org row in this list is one the user has membership in — so the
 * RLS filter passes for those rows. The default org sorts first so
 * the switcher highlights it.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useAuthStore }                   from '@/store/authStore'

export interface OrgMembershipRow {
  id:              string
  organizationId:  string
  organizationName:string
  organizationSlug:string
  role:            string
  isDefault:       boolean
}

interface RawRow {
  id:              string
  role:            string
  is_default:      boolean
  organization_id: string
  organization:    { id: string; name: string; slug: string } | null
}

const FIVE_MIN = 5 * 60 * 1000

export function useUserOrgMemberships(): UseQueryResult<OrgMembershipRow[]> {
  const supabase = useSupabase()
  const userId   = useAuthStore((s) => s.user?.id ?? null)

  return useQuery({
    queryKey:  ['user-org-memberships', userId],
    enabled:   !!userId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<OrgMembershipRow[]> => {
      if (!userId) return []
      const { data, error } = await supabase
        .from('user_org_memberships')
        .select('id, role, is_default, organization_id, organization:organizations(id, name, slug)')
        .eq('user_id', userId)
        .eq('is_active', true)
      if (error) throw error
      const rows = (data ?? []) as unknown as RawRow[]
      return rows
        .filter((r) => r.organization !== null)
        .map((r) => ({
          id:               r.id,
          organizationId:   r.organization_id,
          organizationName: r.organization!.name,
          organizationSlug: r.organization!.slug,
          role:             r.role,
          isDefault:        r.is_default,
        }))
        .sort((a, b) => {
          // Default org first, then alphabetical.
          if (a.isDefault && !b.isDefault) return -1
          if (b.isDefault && !a.isDefault) return  1
          return a.organizationName.localeCompare(b.organizationName)
        })
    },
  })
}
